import React, { useMemo, useEffect, useState } from 'react';

/**
 * SingleCombo Merchant Terms Page (single file)
 * - Sticky Table of Contents (like legal docs)
 * - Smooth scroll to sections
 * - "Last Updated" + print button
 * - Clean typography similar to legal portals
 *
 * Usage:
 *   <MerchantTermsPage />
 */

const MerchantTermsPage = () => {
  const lastUpdated = 'August 23, 2026';

  const sections = useMemo(
    () => [
      { id: 'top', title: 'Merchant Terms of Service — United States' },
      { id: 'important-notice', title: 'Important Notice — Arbitration & Class Action Waiver' },
      { id: '1', title: '1. Parties & Agreement Structure' },
      { id: '2', title: '2. Services Provided; Plans' },
      { id: '3', title: '3. Merchant Locations & Operations' },
      { id: '4', title: '4. Order Flow, Handoff & Title' },
      { id: '5', title: '5. Fees, Payments & Processing' },
      { id: '6', title: '6. Device & Marketing Materials' },
      { id: '7', title: '7. Refunds, Missing & Wrong Food' },
      { id: '8', title: '8. Taxes' },
      { id: '9', title: '9. Placement, Visibility & Ranking' },
      { id: '10', title: '10. Service Standards, Ratings & Enforcement' },
      { id: '11', title: '11. Subscribers & Customer Data' },
      { id: '12', title: '12. Merchant Content & Marketing' },
      { id: '13', title: '13. Acceptable Use & Fraud' },
      { id: '14', title: '14. Confidentiality' },
      { id: '15', title: '15. Data Privacy & Security' },
      { id: '16', title: '16. Permits, Licenses & Insurance' },
      { id: '17', title: '17. Indemnification' },
      { id: '18', title: '18. Governing Law & Dispute Resolution' },
      { id: '19', title: '19. Limitation of Liability' },
      { id: '20', title: '20. Term & Termination' },
      { id: '21', title: '21. Miscellaneous' },
    ],
    []
  );

  const [activeId, setActiveId] = useState('top');

  useEffect(() => {
    const handler = () => {
      // Determine active section based on scroll position
      const offset = 120;
      let current = 'top';

      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top - offset <= 0) current = s.id;
      }
      setActiveId(current);
    };

    handler();
    window.addEventListener('scroll', handler, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', handler, { capture: true });
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const styles = {
    page: {
      background: '#ffffff',
      color: '#111827',
      minHeight: '100vh',
      fontFamily:
        'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
    },
    headerWrap: {
      borderBottom: '1px solid #e5e7eb',
      position: 'sticky',
      top: 0,
      zIndex: 50,
      background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(8px)',
    },
    header: {
      maxWidth: 1200,
      margin: '0 auto',
      padding: '14px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
    },
    brand: {
      display: 'flex',
      flexDirection: 'column',
      lineHeight: 1.1,
    },
    brandTop: { fontWeight: 800, fontSize: 16 },
    brandSub: { fontSize: 12, color: '#6b7280' },
    btnRow: { display: 'flex', gap: 8, alignItems: 'center' },
    button: {
      border: '1px solid #e5e7eb',
      background: '#fff',
      padding: '8px 10px',
      borderRadius: 10,
      fontSize: 13,
      cursor: 'pointer',
      color: '#111827',
    },
    shell: {
      maxWidth: 1200,
      margin: '0 auto',
      padding: '22px 16px 80px',
      display: 'grid',
      gridTemplateColumns: '320px 1fr',
      gap: 24,
    },
    toc: {
      position: 'sticky',
      top: 92,
      alignSelf: 'start',
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      padding: 14,
      background: '#fff',
    },
    tocTitle: { fontWeight: 800, fontSize: 13, color: '#111827', marginBottom: 8 },
    tocLink: (isActive) => ({
      display: 'block',
      padding: '8px 10px',
      borderRadius: 10,
      fontSize: 13,
      color: isActive ? '#111827' : '#374151',
      background: isActive ? '#f3f4f6' : 'transparent',
      cursor: 'pointer',
      textDecoration: 'none',
      lineHeight: 1.3,
      border: '1px solid transparent',
    }),
    contentCard: {
      border: '1px solid #e5e7eb',
      borderRadius: 14,
      background: '#fff',
      padding: '24px 22px',
    },
    h1: { fontSize: 26, margin: 0, fontWeight: 900, letterSpacing: '-0.02em' },
    meta: { marginTop: 8, color: '#6b7280', fontSize: 13 },
    hr: { border: 'none', borderTop: '1px solid #e5e7eb', margin: '18px 0' },
    h2: { fontSize: 18, margin: '26px 0 10px', fontWeight: 900, letterSpacing: '-0.01em' },
    p: { fontSize: 14.5, lineHeight: 1.75, margin: '10px 0', color: '#111827' },
    strongNotice: {
      border: '1px solid #fde68a',
      background: '#fffbeb',
      padding: 14,
      borderRadius: 12,
      fontSize: 14,
      lineHeight: 1.7,
    },
    ul: { margin: '10px 0 10px 22px', fontSize: 14.5, lineHeight: 1.75 },
    small: { fontSize: 12.5, color: '#6b7280', lineHeight: 1.6 },
    footer: {
      borderTop: '1px solid #e5e7eb',
      padding: '16px 16px',
      color: '#6b7280',
      fontSize: 12.5,
      maxWidth: 1200,
      margin: '0 auto',
    },
    mobileHint: {
      display: 'none',
    },
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.headerWrap}>
        <div style={styles.header}>
          <div style={styles.brand}>
            <div style={styles.brandTop}>SingleCombo</div>
            <div style={styles.brandSub}>Merchant Legal • United States</div>
          </div>

          <div style={styles.btnRow}>
            <button style={styles.button} onClick={() => scrollTo('top')} aria-label='Back to top'>
              Back to top
            </button>
            <button style={styles.button} onClick={() => window.print()} aria-label='Print terms'>
              Print
            </button>
          </div>
        </div>
      </div>

      <div style={styles.shell}>
        {/* TOC */}
        <aside style={styles.toc}>
          <div style={styles.tocTitle}>On this page</div>
          {sections
            .filter((s) => s.id !== 'top')
            .map((s) => (
              <div
                key={s.id}
                style={styles.tocLink(activeId === s.id)}
                onClick={() => scrollTo(s.id)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') scrollTo(s.id);
                }}
              >
                {s.title}
              </div>
            ))}
          <div style={{ marginTop: 10 }}>
            <div style={styles.small}>
              Tip: use your browser search (Ctrl/⌘ + F) to find keywords.
            </div>
          </div>
        </aside>

        {/* Content */}
        <main style={styles.contentCard}>
          <div id='top' />
          <h1 style={styles.h1}>MERCHANT TERMS OF SERVICE — UNITED STATES</h1>
          <div style={styles.meta}>
            <strong>SingleCombo LLC</strong> • Merchants • Last Updated:{' '}
            <strong>{lastUpdated}</strong>
          </div>

          <hr style={styles.hr} />

          <div id='important-notice' />
          <h2 style={styles.h2}>Important Notice — Arbitration & Class Action Waiver</h2>
          <div style={styles.strongNotice}>
            THIS AGREEMENT CONTAINS AN <strong>ARBITRATION AGREEMENT</strong> AND A{' '}
            <strong>WAIVER OF CLASS ACTIONS</strong> THAT AFFECT YOUR LEGAL RIGHTS. PLEASE READ{' '}
            <strong>SECTION 18</strong> CAREFULLY.
          </div>

          <div id='1' />
          <h2 style={styles.h2}>1. Parties & Agreement Structure</h2>
          <p style={styles.p}>
            These Merchant Terms of Service (“Terms”) are a contract between{' '}
            <strong>SingleCombo LLC</strong>, an Illinois limited liability company (“SingleCombo,”
            “Company,” “Platform,” “we,” “us”) and the restaurant or food business accepting these
            Terms (“Merchant,” “Restaurant,” “you”).
          </p>
          <p style={styles.p}>
            This agreement (the “Agreement”) includes: (a) these Terms; (b) any signed Merchant
            Agreement, Sign-Up Sheet, Order Form, or Rate Card; (c) any Product Addenda; and (d) any
            policies or exhibits referenced in these Terms or published in the Merchant Portal.
          </p>
          <p style={styles.p}>
            In the event of a conflict, the order of precedence is: (1) the signed Merchant
            Agreement / Order Form / Rate Card (which controls as to fees and plan selection), (2)
            Product Addenda, (3) these Terms.
          </p>

          <div id='2' />
          <h2 style={styles.h2}>2. Services Provided; Plans</h2>
          <p style={styles.p}>
            SingleCombo provides a technology platform that may include online ordering, menu
            hosting, order transmission, marketing tools, customer support tools, subscriber and
            follower features, and delivery coordination (collectively, the “Services”).
          </p>
          <p style={styles.p}>Merchant participates under one of two plans, selected at sign-up:</p>
          <ul style={styles.ul}>
            <li>
              <strong>Basic Plan (Restaurant Delivery):</strong> Merchant fulfills deliveries with
              its own properly licensed and insured drivers. SingleCombo provides order routing,
              tracking tools, and payment processing.
            </li>
            <li>
              <strong>Enterprise Plan (Partner Delivery):</strong> SingleCombo or its delivery
              partners fulfill deliveries. Enterprise Merchants are eligible for SavePass ($0
              delivery fees to customers, funded by SingleCombo), referral earnings under the
              Referral Program Terms, and AI sales prediction and promotion tools when released.
            </li>
          </ul>
          <p style={styles.p}>
            No alcohol or age-restricted product services are offered under this Agreement.
          </p>

          <div id='3' />
          <h2 style={styles.h2}>3. Merchant Locations & Operations</h2>
          <p style={styles.p}>
            This Agreement applies to each restaurant or kitchen location added to the SingleCombo
            platform (each, a “Merchant Location”).
          </p>
          <p style={styles.p}>
            Merchant must maintain accurate hours (including holiday hours), menus, pricing, item
            availability, and prep times, must mark items out of stock promptly, and must train
            staff to properly accept, prepare, and hand off SingleCombo orders. Merchant may not
            list platform prices higher than its in-store prices for the same items unless
            SingleCombo expressly permits.
          </p>
          <p style={styles.p}>
            Merchant is solely responsible for food safety, allergen disclosures, labeling, secure
            packaging, and compliance with all applicable laws and health regulations.
          </p>

          <div id='4' />
          <h2 style={styles.h2}>4. Order Flow, Handoff & Title</h2>
          <p style={styles.p}>
            Orders are transmitted electronically. Merchant must promptly accept or reject orders
            based on availability, and accepted orders must be ready by the quoted prep time.
          </p>
          <p style={styles.p}>
            <strong>Delivery Orders (Enterprise Plan):</strong> Title and risk pass to the customer
            when food is handed to the driver at the Merchant Location.
          </p>
          <p style={styles.p}>
            <strong>Delivery Orders (Basic Plan):</strong> Merchant retains responsibility through
            delivery; title and risk pass when food is handed to the customer.
          </p>
          <p style={styles.p}>
            <strong>Pickup Orders:</strong> Title and risk pass when food is handed to the customer.
          </p>
          <p style={styles.p}>
            SingleCombo does not take ownership of Merchant products at any time. Merchant — not
            SingleCombo — is the seller of record for all food and beverages.
          </p>

          <div id='5' />
          <h2 style={styles.h2}>5. Fees, Payments & Processing</h2>
          <p style={styles.p}>
            Merchant agrees to pay the fees described in the applicable signed Merchant Agreement,
            Order Form, or Rate Card, which may include: commission on Net Sales, payment processing
            fees, platform fees, monthly account fees, weekly device and marketing materials fees,
            and Merchant-funded promotion costs. “Net Sales” means the gross order subtotal (food
            and beverage) excluding taxes, tips, delivery fees, and SingleCombo-funded promotions.
          </p>
          <p style={styles.p}>
            SingleCombo uses a third-party payment processor to collect customer payments, deduct
            applicable fees, refunds, chargebacks, and adjustments, and remit net payouts to
            Merchant on a weekly basis. Payout timing depends on the payment processor and
            Merchant’s bank. SingleCombo may hold or delay payouts where fraud, excessive
            chargebacks, or disputes are suspected.
          </p>
          <p style={styles.p}>
            SingleCombo may change fees on <strong>30 days’</strong> written notice; continued use
            of the Services after the effective date constitutes acceptance. Merchant must report
            payment or order disputes within <strong>14 days</strong> of the transaction date.
          </p>

          <div id='6' />
          <h2 style={styles.h2}>6. Device & Marketing Materials</h2>
          <p style={styles.p}>
            SingleCombo provides Merchant with an order-management tablet and marketing materials
            (stickers, flyers, table tents, and similar) for the weekly fee stated in Merchant’s
            plan, deducted from payouts.
          </p>
          <p style={styles.p}>
            The tablet is and remains the exclusive property of SingleCombo LLC. It is provided
            solely for managing SingleCombo orders and Merchant Portal functions and may not be used
            for any other purpose, including personal use, browsing, installing unauthorized apps,
            or use with other platforms. Merchant may not sell, lease, lend, modify, tamper with, or
            remove the device from the Merchant Location. SingleCombo may remotely manage, update,
            monitor, lock, or disable the device.
          </p>
          <p style={styles.p}>
            Upon closure, suspension, or termination of Merchant’s account for any reason, the
            device must be returned in working condition within <strong>14 days</strong> using the
            return method SingleCombo provides. Unreturned or damaged devices will be charged to
            Merchant at replacement cost, which may be offset against final payout.
          </p>

          <div id='7' />
          <h2 style={styles.h2}>7. Refunds, Missing & Wrong Food</h2>
          <p style={styles.p}>
            Merchant is financially responsible for refunds, credits, or reorders resulting from
            missing items, incorrect items, incomplete orders, late preparation, cancellations after
            acceptance, or Merchant preparation errors. Basic Plan Merchants are also responsible
            for all delivery-related refunds.
          </p>
          <p style={styles.p}>
            SingleCombo will cover refunds caused solely by platform or technical errors, and, for
            Enterprise Plan orders, refunds caused solely by partner-driver error. SingleCombo may
            issue customer credits and deduct the applicable amount from Merchant payouts when
            Merchant is at fault.
          </p>

          <div id='8' />
          <h2 style={styles.h2}>8. Taxes</h2>
          <p style={styles.p}>
            SingleCombo collects all applicable State and local sales taxes from customers at
            checkout on every platform order. Where SingleCombo qualifies as a marketplace
            facilitator under applicable law (in Illinois, currently $100,000 in marketplace sales
            in a 12-month period), SingleCombo is responsible for remitting those taxes to the
            Illinois Department of Revenue and applicable local authorities, including Chicago-area
            restaurant and MPEA taxes where required. Until that threshold is met, taxes collected
            on platform orders are passed through to Merchant in payouts and Merchant is responsible
            for remitting them.
          </p>
          <p style={styles.p}>
            Merchant remains solely responsible for taxes on all non-platform sales (in-store,
            phone, Merchant’s own website), and for all income, payroll, franchise, and other taxes
            arising from Merchant’s own operations. Merchant must provide accurate tax rate and
            location information and its Illinois Business Tax number at onboarding. SingleCombo
            makes tax collection reports available in the Merchant Portal.
          </p>

          <div id='9' />
          <h2 style={styles.h2}>9. Placement, Visibility & Ranking</h2>
          <p style={styles.p}>
            SingleCombo retains sole and absolute discretion over the order, ranking, and placement
            of Merchant’s store anywhere on the platform, including in “Your Pick,” “Neighborhood
            Favorites,” “Near You,” “Order Again,” search results, and promotional sections. No
            placement is guaranteed. Placement is determined by SingleCombo’s algorithms using
            factors including customer behavior, order history, ratings, delivery performance,
            distance, and Merchant’s Service Standard compliance.
          </p>
          <p style={styles.p}>
            A customer being a Subscriber or Follower of Merchant’s store — including customers
            Merchant invited to the platform — does <strong>not</strong> entitle Merchant to top
            placement or any particular visibility in that customer’s app. If a customer has not
            ordered from Merchant’s store within a rolling period established by SingleCombo
            (currently 90 days, subject to change), SingleCombo may remove Merchant’s store from
            that customer’s “Your Pick” and other personalized sections, even if that customer
            remains a Subscriber.
          </p>

          <div id='10' />
          <h2 style={styles.h2}>10. Service Standards, Ratings & Enforcement</h2>
          <p style={styles.p}>
            SingleCombo monitors, at minimum: average customer rating, order accuracy rate,
            cancellation rate, on-time preparation rate, and (for Basic Plan) on-time delivery rate
            and delivery complaints. Current thresholds are published in the Merchant Portal and may
            be updated on 14 days’ notice.
          </p>
          <p style={styles.p}>
            If a Basic Plan Merchant fails to deliver orders on time or receives repeated
            delivery-related complaints, SingleCombo may, at its sole discretion: issue a warning;
            reduce Merchant’s visibility; convert Merchant’s store to a <strong>Pickup-Only</strong>{' '}
            store; suspend the store; or permanently unlist Merchant from the platform.
          </p>
          <p style={styles.p}>
            Poor customer ratings, repeated complaints, food-safety complaints, or Service Standard
            violations may result in suspension or unlisting of Merchant’s store{' '}
            <strong>regardless of the number of Subscribers or Followers</strong> Merchant has.
            SingleCombo may suspend Merchant immediately and without notice for credible food-safety
            reports, health department action, fraud, abusive conduct, or violation of law.
          </p>

          <div id='11' />
          <h2 style={styles.h2}>11. Subscribers & Customer Data</h2>
          <p style={styles.p}>
            SingleCombo owns and retains full rights to all Subscriber, Follower, and customer
            relationships, accounts, and data generated on the platform, including customers invited
            or referred by Merchant. SingleCombo may keep Subscribers untracked or anonymized;
            Merchant receives only the minimum order details required to prepare and (for Basic
            Plan) deliver an order.
          </p>
          <p style={styles.p}>
            Subscriber and Follower relationships expire automatically after a maximum of{' '}
            <strong>two (2) years</strong> from the date of subscription unless renewed by the
            customer’s own action, and may expire earlier for inactivity, customer request, or
            platform policy. Merchant acquires no vested right in any Subscriber.
          </p>
          <p style={styles.p}>
            Merchant may only use customer data to fulfill the specific order, must protect it using
            reasonable safeguards, and may not harvest, store, sell, or share customer data, solicit
            customers to order off-platform, or contact customers except through platform-provided
            tools. Merchant must notify SingleCombo promptly of any suspected data breach and must
            comply with applicable privacy laws.
          </p>

          <div id='12' />
          <h2 style={styles.h2}>12. Merchant Content & Marketing</h2>
          <p style={styles.p}>
            Merchant grants SingleCombo a royalty-free, non-exclusive, sublicensable license to use
            Merchant content (menus, photos, logos, business details) for listing and marketing on
            the platform and in SingleCombo marketing. SingleCombo may photograph, reformat, or edit
            Merchant content for consistency and may apply dietary, allergen, and promotional
            labels. Merchant warrants its content is accurate, lawful, and non-infringing.
          </p>
          <p style={styles.p}>
            Customers may leave reviews and ratings. Merchant may not offer incentives for reviews.
          </p>

          <div id='13' />
          <h2 style={styles.h2}>13. Acceptable Use & Fraud</h2>
          <p style={styles.p}>
            Merchant shall not create fake or fraudulent orders, manipulate transactions or ratings,
            misuse customer or driver data, or attempt to bypass platform safeguards. SingleCombo
            may suspend or terminate accounts immediately for fraud or abuse.
          </p>

          <div id='14' />
          <h2 style={styles.h2}>14. Confidentiality</h2>
          <p style={styles.p}>
            Each party must protect the other’s confidential business, pricing, and operational
            information and use it only for purposes of performing under this Agreement. SingleCombo
            owns all rights in the platform, its algorithms, data, analytics, AI models, and the
            SingleCombo, SavePass, and Your Pick names. Aggregate and de-identified data derived
            from Merchant’s activity on the platform belongs to SingleCombo.
          </p>

          <div id='15' />
          <h2 style={styles.h2}>15. Data Privacy & Security</h2>
          <p style={styles.p}>
            Merchant may only use customer data to fulfill orders and must protect it using
            reasonable safeguards. Merchant may not sell or share customer data and must notify
            SingleCombo promptly of any suspected data breach. Merchant must comply with all
            applicable privacy laws, including the Illinois Personal Information Protection Act and,
            where applicable, the Illinois Biometric Information Privacy Act.
          </p>

          <div id='16' />
          <h2 style={styles.h2}>16. Permits, Licenses & Insurance</h2>
          <p style={styles.p}>
            Merchant represents and warrants that it holds, and will maintain in good standing, all
            permits, licenses, certifications, and registrations required to operate its business
            and sell food, including its retail food establishment license, health department
            certification, food service sanitation manager certification, and business/sales tax
            registration. Merchant must notify SingleCombo within 48 hours of any suspension,
            revocation, or failed inspection.
          </p>
          <p style={styles.p}>
            Merchant must maintain, at its own cost: Commercial General Liability insurance
            (including products and completed operations) of at least $1,000,000 per occurrence /
            $2,000,000 aggregate, naming SingleCombo LLC as an additional insured; Workers’
            Compensation insurance as required by law; and, for Basic Plan Merchants, Commercial
            Auto or Hired & Non-Owned Auto coverage of at least $1,000,000 covering delivery
            drivers. Certificates of insurance must be provided at onboarding and upon renewal;
            lapse of required insurance is grounds for immediate suspension.
          </p>
          <p style={styles.p}>
            Basic Plan Merchants are solely responsible for their drivers, including classification,
            wages, background checks, vehicle safety, licensing, insurance, and conduct. Merchant’s
            drivers are not SingleCombo employees, agents, or contractors.
          </p>

          <div id='17' />
          <h2 style={styles.h2}>17. Indemnification</h2>
          <p style={styles.p}>
            Merchant is fully responsible and liable for any claim, lawsuit, injury, illness, food
            poisoning, allergic reaction, or damage arising from or relating to food or beverages
            supplied by Merchant. Merchant agrees to defend, indemnify, and hold harmless
            SingleCombo, its affiliates, officers, employees, contractors, and delivery partners
            against claims arising from: food safety or allergen issues; Merchant negligence or
            misconduct; Merchant’s drivers (Basic Plan); inaccurate menus or pricing; Merchant’s
            taxes; or violation of law. Merchant must notify SingleCombo within 24 hours of becoming
            aware of any such claim or any health-department action.
          </p>
          <p style={styles.p}>
            SingleCombo will indemnify Merchant for third-party intellectual property claims arising
            solely from SingleCombo technology.
          </p>

          <div id='18' />
          <h2 style={styles.h2}>18. Governing Law & Dispute Resolution</h2>
          <p style={styles.p}>
            This Agreement is governed by the laws of the <strong>State of Illinois</strong>. The
            parties will attempt good-faith resolution first for at least 30 days. If not resolved,
            disputes will be resolved by binding arbitration administered by AAA or JAMS in{' '}
            <strong>Cook County, Illinois</strong>, unless otherwise required by law.
          </p>
          <p style={styles.p}>
            <strong>Class Action Waiver:</strong> All disputes must be brought individually. No
            class or representative actions are permitted.
          </p>

          <div id='19' />
          <h2 style={styles.h2}>19. Limitation of Liability</h2>
          <p style={styles.p}>
            Services are provided “as is” and “as available” without warranties of any kind.
            SingleCombo does not guarantee any volume of orders, customers, Subscribers, revenue,
            placement, or uptime. Except for fraud, unpaid fees, or indemnification obligations,
            neither party is liable for indirect, incidental, special, consequential, or punitive
            damages or lost profits, and total liability of either party shall not exceed the fees
            paid by Merchant to SingleCombo in the previous <strong>6 months</strong>.
          </p>

          <div id='20' />
          <h2 style={styles.h2}>20. Term & Termination</h2>
          <p style={styles.p}>
            This Agreement is effective upon acceptance and continues until terminated. Either party
            may terminate for convenience with <strong>30 days’</strong> written notice; Merchant
            may switch plans on 30 days’ notice effective at the next billing cycle. SingleCombo may
            suspend, convert to Pickup-Only, or terminate immediately for fraud, food-safety risks,
            Service Standard violations, lapse of required permits or insurance, or legal
            violations.
          </p>
          <p style={styles.p}>
            Upon termination: in-flight orders must still be fulfilled; all Subscriber and Follower
            relationships remain with SingleCombo and do not transfer to Merchant; the device must
            be returned per Section 6; and outstanding fees, refunds, and chargebacks may be offset
            against the final payout, which is released within 30 days subject to a reserve for
            disputes. Sections 11, 14, 15, 17, 18, and 19 survive termination.
          </p>

          <div id='21' />
          <h2 style={styles.h2}>21. Miscellaneous</h2>
          <ul style={styles.ul}>
            <li>Independent contractor relationship; no partnership, agency, or franchise.</li>
            <li>
              Non-exclusive: Merchant may use other platforms; SingleCombo may list competing
              merchants.
            </li>
            <li>
              SingleCombo may update these Terms on 30 days’ notice via the Merchant Portal or
              email; continued use constitutes acceptance.
            </li>
            <li>Assignment allowed only with consent or in a merger or sale of business.</li>
            <li>Severability applies; remainder stays effective.</li>
            <li>
              This Agreement, together with the documents in Section 1, is the entire agreement on
              the subject.
            </li>
          </ul>

          <hr style={styles.hr} />
          <div style={styles.small}>
            © {new Date().getFullYear()} SingleCombo LLC. All rights reserved.
          </div>
        </main>
      </div>

      <div style={styles.footer}>SingleCombo LLC • Merchant Terms of Service (United States)</div>

      {/* Print styles */}
      <style>{`
        main [id] {
          scroll-margin-top: 110px;
        }

        @media (max-width: 980px) {
          /* Stack layout on mobile */
          .sc-shell {
            grid-template-columns: 1fr !important;
          }
        }

        @media print {
          /* Hide header + TOC and remove borders for clean printing */
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          button { display: none !important; }
          aside { display: none !important; }
          a { text-decoration: none !important; color: #111827 !important; }
        }
      `}</style>
    </div>
  );
};

export default MerchantTermsPage;
